import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, BookOpen, Check, ChevronDown, Download, FileText, Info, ReceiptText, RefreshCw, X } from 'lucide-react';
import { architectApi, MonthlyRecommendation, PricingMonthlyRow, PricingSheet, MonthlyBenchmarkDocument } from './api';
import { monthlyChargeTerms, monthlyExampleLabel, monthlyQuestionLabel, monthlyRowLabel } from './monthlyPresentation';
import MonthlyPriceInfo from './MonthlyPriceInfo';
import './monthly-recommendation.css';

interface Props {
  projectId: string;
  sheet: PricingSheet;
  disabled?: boolean;
  expanded?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onApply: (rows: PricingMonthlyRow[], expectedMonthlySnapshot: string) => boolean;
}
const positiveMinimumQuestion = (key: string, required: boolean) => required && key.endsWith('.minimum');
const wholeNumberQuestion = (key: string) => /\.(quantity|minimum)$/.test(key);
const monthlySnapshot = (sheet: PricingSheet) => JSON.stringify(sheet.monthly);
const numericAnswer = (value: string): number | null => {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 && n <= 1e7 ? n : null;
};

export default function MonthlyRecommendationPanel({ projectId, sheet, disabled = false, expanded = true, onExpand, onCollapse, onBusyChange, onApply }: Props) {
  const id = useId();
  const [recommendation, setRecommendation] = useState<MonthlyRecommendation | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'analyze' | 'calculate' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answerDirty, setAnswerDirty] = useState(false);
  const [capturedMonthly, setCapturedMonthly] = useState<string | null>(null);
  const [appliedMonthly, setAppliedMonthly] = useState<string | null>(null);
  const capturedSheet = useRef<PricingSheet | null>(null);
  const requestId = useRef(0);
  const answerRevision = useRef(0);
  const autoStarted = useRef<string | null>(null);
  const reapplySnapshot = useRef<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [referenceId, setReferenceId] = useState<string | null>(null);
  const snapshot = monthlySnapshot(sheet);
  const latest = useRef({ projectId, snapshot, fullSnapshot: JSON.stringify(sheet), sheet, onApply });
  latest.current = { projectId, snapshot, fullSnapshot: JSON.stringify(sheet), sheet, onApply };
  const busyCallback = useRef(onBusyChange);
  busyCallback.current = onBusyChange;
  useEffect(() => { busyCallback.current?.(!!busy || libraryOpen || answerDirty || reapplySnapshot.current !== null); }, [busy, libraryOpen, answerDirty, recommendation]);
  useEffect(() => () => { busyCallback.current?.(false); }, []);

  useEffect(() => {
    requestId.current += 1;
    setRecommendation(null);
    setAnswers({});
    setBusy(null);
    setError(null);
    setAnswerDirty(false);
    setCapturedMonthly(null);
    setAppliedMonthly(null);
    capturedSheet.current = null;
    autoStarted.current = null;
    reapplySnapshot.current = null;
    return () => { requestId.current += 1; };
  }, [projectId]);

  const applied = appliedMonthly !== null && appliedMonthly === snapshot;
  const stale = capturedMonthly !== null && capturedMonthly !== snapshot && !applied;
  const questions = useMemo(() => recommendation?.questions ?? [], [recommendation]);
  const invalidAnswers = questions.some(q => {
    const value = answers[q.key] ?? '';
    const parsed = numericAnswer(value);
    return (q.required || value.trim() !== '') && (parsed === null || (wholeNumberQuestion(q.key) && !Number.isInteger(parsed)) || (positiveMinimumQuestion(q.key, q.required) && parsed <= 0));
  });

  const request = useCallback(async (recalculate: boolean) => {
    if (disabled || busy || (recalculate && (stale || invalidAnswers || !recommendation))) return;
    const requestProject = projectId;
    const token = recalculate ? recommendation?.analysisToken : undefined;
    const sourceSheet = recalculate && capturedSheet.current
      ? capturedSheet.current : JSON.parse(JSON.stringify(latest.current.sheet)) as PricingSheet;
    const sourceMonthly = monthlySnapshot(sourceSheet);
    const revision = answerRevision.current;
    const currentRequest = ++requestId.current;
    const numericAnswers: Record<string, number> = {};
    if (recalculate) questions.forEach(q => {
      const value = numericAnswer(answers[q.key] ?? '');
      if (value !== null) numericAnswers[q.key] = value;
    });
    if (!recalculate) {
      capturedSheet.current = sourceSheet;
      setCapturedMonthly(sourceMonthly);
      setAppliedMonthly(null);
      reapplySnapshot.current = null;
      setRecommendation(null);
      setAnswers({});
      setAnswerDirty(false);
    }
    setBusy(recalculate ? 'calculate' : 'analyze');
    setError(null);
    try {
      const input = {
        current_sheet: sourceSheet,
        ...(token ? { analysisToken: token, answers: numericAnswers } : {}),
      };
      let result: { recommendation: MonthlyRecommendation } | null = null;
      for (let attempt = 0; attempt <= 40; attempt += 1) {
        try { result = await architectApi.recommendMonthlyPricing(requestProject, input); break; }
        catch (err: any) {
          if (err?.code !== 'recommendation_busy' || attempt === 40) throw err;
          const delay = Math.max(1, Math.min(10, Number(err.body?.retry_after) || 3));
          await new Promise(resolve => window.setTimeout(resolve, delay * 1000));
          if (currentRequest !== requestId.current || latest.current.projectId !== requestProject) return;
        }
      }
      if (!result) return;
      if (currentRequest !== requestId.current || latest.current.projectId !== requestProject) return;
      if (revision !== answerRevision.current && recalculate) {
        setAnswerDirty(true);
        setError('Your inputs changed during the calculation. Recalculate to use the latest values.');
        return;
      }
      setRecommendation(result.recommendation);
      setAnswers(Object.fromEntries(result.recommendation.questions.map(q => [q.key, q.value == null ? '' : String(q.value)])));
      setAnswerDirty(false);
      // A new project can receive its first monthly draft automatically. A late
      // response must never replace a price, setup or volume edited meanwhile.
      const autoApplySnapshot = !recalculate && !sourceSheet.monthly.length ? JSON.stringify(sourceSheet) : recalculate ? reapplySnapshot.current : null;
      if (autoApplySnapshot && result.recommendation.applyReady && latest.current.fullSnapshot === autoApplySnapshot) {
        const rows = JSON.parse(JSON.stringify([...(result.recommendation.preservedBaseline ?? []), ...result.recommendation.rows])) as PricingMonthlyRow[];
        if (latest.current.onApply(rows, autoApplySnapshot)) {
          setAppliedMonthly(JSON.stringify(rows));
          reapplySnapshot.current = null;
        }
      }
    } catch (err: any) {
      if (currentRequest === requestId.current && latest.current.projectId === requestProject) {
        setError(err?.message || 'The monthly structure could not be prepared. Try again.');
      }
    } finally {
      if (currentRequest === requestId.current) setBusy(null);
    }
  }, [disabled, busy, stale, invalidAnswers, recommendation, projectId, questions, answers]);

  useEffect(() => {
    if (!expanded || disabled || busy || autoStarted.current === projectId) return;
    autoStarted.current = projectId;
    void request(false);
  }, [projectId, disabled, expanded, busy, request]);

  const apply = async () => {
    if (!recommendation?.applyReady || busy || disabled || stale || answerDirty || invalidAnswers || applied || capturedMonthly === null || !capturedSheet.current) return;
    const sourceSheet = JSON.parse(JSON.stringify(latest.current.sheet)) as PricingSheet;
    const expectedSnapshot = JSON.stringify(sourceSheet);
    const requestProject = projectId;
    const revision = answerRevision.current;
    const currentRequest = ++requestId.current;
    const numericAnswers: Record<string, number> = {};
    questions.forEach(q => {
      const value = numericAnswer(answers[q.key] ?? '');
      if (value !== null) numericAnswers[q.key] = value;
    });
    setBusy('apply');
    setError(null);
    try {
      // Recheck the approved scope and saved baseline immediately before Apply.
      // The analysis token reuses extraction; this does not run the model again.
      const result = await architectApi.recommendMonthlyPricing(requestProject, {
        current_sheet: sourceSheet,
        analysisToken: recommendation.analysisToken,
        answers: numericAnswers,
      });
      if (currentRequest !== requestId.current || latest.current.projectId !== requestProject) return;
      if (latest.current.fullSnapshot !== expectedSnapshot || answerRevision.current !== revision) {
        setError('Your draft or inputs changed during the check. Review the latest values before applying.');
        return;
      }
      const checked = result.recommendation;
      setRecommendation(checked);
      if (!checked.applyReady) {
        setError('This structure needs another review before it can be applied.');
        return;
      }
      const rows = JSON.parse(JSON.stringify([...(checked.preservedBaseline ?? []), ...checked.rows])) as PricingMonthlyRow[];
      if (onApply(rows, expectedSnapshot)) {
        setAppliedMonthly(JSON.stringify(rows));
        reapplySnapshot.current = null;
        setError(null);
      } else setError('Your monthly draft changed. Analyze again before replacing it.');
    } catch (err: any) {
      if (currentRequest === requestId.current && latest.current.projectId === requestProject) {
        setError(err?.message || 'The source could not be verified. Analyze again before applying.');
      }
    } finally {
      if (currentRequest === requestId.current) setBusy(null);
    }
  };

  const tableRows = recommendation ? [...(recommendation.preservedBaseline ?? []), ...recommendation.rows] : [];
  const missingVolumes = questions.some(q => q.value == null);
  const groupedQuestions = questions.reduce<Record<string, typeof questions>>((groups, question) => {
    const chargeId = question.key.split('.')[0];
    const row = recommendation?.rows.find(item => item.simple?.charges.some(charge => charge.id === chargeId));
    const bucket = row?.simple?.bucket;
    const group = bucket === 'communication' ? 'Communication' : bucket === 'processing' ? 'Monthly activity' : 'Service terms';
    (groups[group] ??= []).push(question);
    return groups;
  }, {});
  return (
    <section className="arch-monthly-rec" aria-label="Monthly pricing references" aria-busy={!!busy}>
      <div className="arch-monthly-rec-toolbar">
        <button type="button" className="arch-btn arch-monthly-rec-library-button" onClick={() => { setReferenceId(null); setLibraryOpen(true); }}><BookOpen size={15} aria-hidden="true" />Reference library</button>
        {onExpand && (!expanded || onCollapse) ? <button type="button" className="arch-monthly-rec-toggle" aria-expanded={expanded} aria-controls={`${id}-preview`} disabled={!!busy || (expanded && answerDirty)} onClick={() => expanded ? onCollapse?.() : onExpand()}>
          {expanded ? 'Close preview' : 'Review prices'}<ChevronDown size={15} aria-hidden="true" />
        </button> : null}
      </div>
      <div id={`${id}-preview`} className="arch-monthly-rec-preview" hidden={!expanded}>
      <div className="arch-monthly-rec-intro">
        <h3 id={`${id}-title`}>Price preview</h3>
        <button type="button" className="arch-monthly-rec-refresh" disabled={disabled || !!busy} onClick={() => void request(false)} aria-label={busy === 'analyze' ? 'Preparing monthly structure' : recommendation ? 'Refresh monthly structure' : 'Retry monthly structure'} title="Refresh using the approved estimate">
          <RefreshCw size={16} className={busy === 'analyze' ? 'arch-monthly-rec-spin' : ''} aria-hidden="true" />
        </button>
      </div>
      {busy === 'analyze' ? <div className="arch-monthly-rec-loading" role="status"><div className="arch-monthly-rec-loading-orb"><ReceiptText size={23} aria-hidden="true" /></div><strong>Preparing prices…</strong><div className="arch-monthly-rec-skeleton" aria-hidden="true"><i /><i /><i /></div></div> : null}
      {stale ? <p className="arch-monthly-rec-notice" role="status">Your monthly draft has changed. Refresh to update prices.</p> : null}
      {error ? <p className="arch-monthly-rec-error" role="alert">{error}</p> : null}
      {recommendation ? (
        <div className="arch-monthly-rec-result">
          {questions.length ? <details className="arch-monthly-rec-questions" open={missingVolumes || answerDirty ? true : undefined}>
            <summary><Info size={15} aria-hidden="true" /><span>{missingVolumes ? 'Enter monthly volumes' : 'Monthly volumes'}</span><ChevronDown size={15} aria-hidden="true" /></summary>
            {missingVolumes ? <p>Enter the missing volumes to calculate prices.</p> : null}
            {Object.entries(groupedQuestions).map(([group, fields]) => <fieldset key={group}><legend>{group}</legend><div className="arch-monthly-rec-inputs">
              {fields.map(q => { const index = questions.indexOf(q); return <label key={q.key} htmlFor={`${id}-q-${index}`}>
                <span>{monthlyQuestionLabel(q, recommendation.rows)}{q.required ? <small>Required</small> : null}</span>
                <div className="arch-monthly-rec-input-wrap">
                  <input id={`${id}-q-${index}`} aria-label={monthlyQuestionLabel(q, recommendation.rows)} aria-required={q.required} type="number" min={positiveMinimumQuestion(q.key, q.required) ? '1' : '0'} max="10000000" step={wholeNumberQuestion(q.key) ? '1' : 'any'} inputMode={wholeNumberQuestion(q.key) ? 'numeric' : 'decimal'} value={answers[q.key] ?? ''} placeholder="Enter volume" disabled={disabled} onChange={e => {
                    if (applied) {
                      reapplySnapshot.current = latest.current.fullSnapshot;
                      setCapturedMonthly(latest.current.snapshot);
                      setAppliedMonthly(null);
                    }
                    answerRevision.current += 1;
                    setAnswers(current => ({ ...current, [q.key]: e.target.value }));
                    setAnswerDirty(true);
                  }} />
                  <span>{q.unit}</span>
                </div>
              </label>; })}
            </div></fieldset>)}
            <div className="arch-monthly-rec-question-actions">
              <button type="button" className="arch-btn arch-btn-sm" disabled={disabled || !!busy || stale || applied || invalidAnswers || !answerDirty} onClick={() => void request(true)}>
                <RefreshCw size={14} className={busy === 'calculate' ? 'arch-monthly-rec-spin' : ''} aria-hidden="true" />
                {busy === 'calculate' ? 'Calculating…' : 'Update example'}
              </button>
            </div>
          </details> : null}
          {recommendation.preservedBaseline?.length ? <p className="arch-monthly-rec-baseline"><Check size={14} aria-hidden="true" />{recommendation.preservedBaseline.length} existing {recommendation.preservedBaseline.length === 1 ? 'service' : 'services'} unchanged.</p> : null}
          <div className="arch-monthly-rec-table-wrap">
            <table className="arch-monthly-rec-table" aria-label="Recommended Simple Structure">
              <thead><tr><th>Service</th><th>Price</th></tr></thead>
              <tbody>{tableRows.map((row, index) => <tr key={`${row.label}-${index}`}>
                <th scope="row">{monthlyRowLabel(row)}</th>
                <td><div className="arch-monthly-rec-price"><div>{row.simple ? row.simple.charges.map(charge => <div key={charge.id}>{monthlyChargeTerms(charge)}</div>) : row.price || 'To confirm'}</div><MonthlyPriceInfo row={row} recommendation={recommendation.rows.includes(row) ? recommendation : null} onOpenReference={sourceId => { setReferenceId(sourceId); setLibraryOpen(true); }} /></div></td>
              </tr>)}
                <tr className="arch-monthly-rec-example"><th scope="row">Example month</th><td>{monthlyExampleLabel(recommendation.exampleMonth)}{recommendation.exampleMonth.total !== null && recommendation.exampleMonth.formula ? <details className="arch-monthly-rec-calculation"><summary>Calculation</summary><p>{recommendation.exampleMonth.formula}</p></details> : null}</td></tr>
              </tbody>
            </table>
          </div>
          {recommendation.warnings.length ? <details className="arch-monthly-rec-details" >
            <summary><Info size={14} aria-hidden="true" /><span>{recommendation.applyReady ? 'Review' : 'Review required'} · {recommendation.warnings.length}</span><ChevronDown size={14} aria-hidden="true" /></summary>
            <ul>{recommendation.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
          </details> : null}
          <div className="arch-monthly-rec-apply">
            <button type="button" className="arch-btn arch-btn-primary" onClick={() => void apply()} disabled={disabled || !!busy || stale || answerDirty || invalidAnswers || !recommendation.applyReady || applied}>
              {applied ? <Check size={15} aria-hidden="true" /> : <ArrowRight size={15} aria-hidden="true" />}{applied ? 'Added to draft' : busy === 'apply' ? 'Checking…' : 'Use prices'}
            </button>
          </div>
        </div>
      ) : null}
      </div>
      {libraryOpen ? <MonthlyReferenceLibrary initialId={referenceId} matchingIds={recommendation?.benchmarks.map(source => source.id) ?? []} onClose={() => setLibraryOpen(false)} /> : null}
    </section>
  );
}

function MonthlyReferenceLibrary({ initialId, matchingIds, onClose }: { initialId: string | null; matchingIds: string[]; onClose: () => void }) {
  const [documents, setDocuments] = useState<MonthlyBenchmarkDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'estimates' | 'rates'>('estimates');
  const [downloading, setDownloading] = useState<string | null>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    let live = true;
    void architectApi.getMonthlyPricingLibrary().then(result => {
      if (!live) return;
      setDocuments(result.documents);
      setSelected([...(initialId ? [initialId] : []), ...matchingIds].find(id => result.documents.some(document => document.id === id)) ?? result.documents[0]?.id ?? null);
    }).catch(err => { if (live) setError(err?.message || 'The reference estimates could not be loaded.'); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // Match the reference used when the dialog opens; later calculations do not move the reader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); close.current(); }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input, [tabindex="0"]') ?? []).filter(element => element.getClientRects().length > 0);
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', onKey); previousFocus?.focus({ preventScroll: true }); };
  }, []);

  const download = async (source: MonthlyBenchmarkDocument) => {
    if (downloading) return;
    setDownloading(source.id); setError(null);
    try {
      const blob = await architectApi.downloadMonthlyPricingBenchmark(source.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = source.filename; anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err: any) { setError(err?.message || 'The reference estimate could not be downloaded.'); }
    finally { setDownloading(null); }
  };
  const current = documents.find(source => source.id === selected);
  return createPortal(<div className="architect-scope arch-monthly-library-host"><div className="arch-monthly-library-overlay" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div ref={dialog} className="arch-monthly-library" role="dialog" aria-modal="true" aria-labelledby="monthly-library-title">
      <header><span className="arch-monthly-rec-mark"><BookOpen size={21} aria-hidden="true" /></span><div><h2 id="monthly-library-title">Reference estimates</h2></div><button ref={closeButton} type="button" className="arch-monthly-library-close" onClick={onClose} aria-label="Close reference estimates"><X size={20} /></button></header>
      <div className="arch-monthly-library-tabs" role="tablist" aria-label="Pricing references"><button type="button" role="tab" aria-selected={tab === 'estimates'} onClick={() => setTab('estimates')}>Client estimates <span>{loading ? '…' : documents.length}</span></button><button type="button" role="tab" aria-selected={tab === 'rates'} onClick={() => setTab('rates')}>Standard rates</button></div>
      {error ? <p className="arch-monthly-rec-error" role="alert">{error}</p> : null}
      {tab === 'estimates' ? <div className="arch-monthly-library-content" role="tabpanel" aria-label="Client estimates">
        {loading ? <p role="status">Loading the reference library…</p> : <><nav className="arch-monthly-library-list" aria-label="Reference documents">{documents.map(source => <button key={source.id} type="button" aria-pressed={selected === source.id} onClick={() => setSelected(source.id)}><FileText size={16} aria-hidden="true" /><span><strong>{source.client || source.filename.replace(/\.docx$/i, '')}</strong>{matchingIds.includes(source.id) ? <small>Used here</small> : null}</span>{matchingIds.includes(source.id) ? <Check size={14} aria-label="Comparable estimate" /> : null}</button>)}</nav>{current ? <article className="arch-monthly-library-preview"><div className="arch-monthly-library-preview-head"><div><h3>{current.client}</h3><p>{current.filename}</p></div><button type="button" className="arch-btn" disabled={!!downloading} onClick={() => void download(current)}><Download size={15} aria-hidden="true" />{downloading === current.id ? 'Downloading…' : 'Download DOCX'}</button></div><div className="arch-monthly-rec-table-wrap"><table className="arch-monthly-rec-table"><thead><tr><th>Service</th><th>Price</th></tr></thead><tbody>{current.actualRecommendedSimpleStructure.map((row, index) => <tr key={index}><th scope="row">{row.label}</th><td>{row.price}</td></tr>)}</tbody></table></div><ReferenceNotes document={current}/><div className="arch-monthly-rec-derivation"><h4>How this structure was derived</h4><p>{current.derivationText}</p></div></article> : null}</>}
      </div> : <div className="arch-monthly-library-rates" role="tabpanel" aria-label="Standard rates"><dl>
        <div><dt>Communication</dt><dd>SMS $3 per contact; email $1; CRM chase bundle $4 per active lead. Unlimited monthly messages. Voice $1 per 2-minute call, with a confirmed monthly minimum.</dd></div>
        <div><dt>Processing</dt><dd>Heavy work $5 per active unit; light, bounded work $2; a generated document from several source files $10. Full mailbox monitoring $300 per mailbox.</dd></div>
        <div><dt>Platform</dt><dd>One fee: $500 for a single-system dashboard; $750 for multiple systems, storage, telephony or private hosting; $850 for 3–5 systems with heavy sync or pipeline compute.</dd></div>
        <div><dt>Fixed packages</dt><dd>Hosting $500–$750; unlimited AP invoices $1,000; a secondary document stream $500. Payroll $250 up to 50 employees, then $25 per extra 5.</dd></div>
        <div><dt>Third parties & new phases</dt><dd>Third-party fees pass through at cost; per-record services add 20%. Unknown costs stay TBD and excluded. A new phase preserves existing lines and adds one flat add-on plus one additional-user line.</dd></div>
      </dl></div>}
    </div>
  </div></div>, document.body);
}

export function ReferenceNotes({ document }: { document: MonthlyBenchmarkDocument }) {
  const notes = document.flaggedConflictsWithCurrentRules;
  if (!notes?.length) return null;
  return <section className="monthly-reference-notes" aria-label="Reference notes"><h4>Reference notes</h4><ul>{notes.map(note => <li key={note.code}>{note.detail}</li>)}</ul></section>;
}
